//libraries
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { ToolbarProps } from "../../types/editor";

// components
import ToolbarButton from "./ToolbarButton";
import FontFamilyDropdown from "./FontFamilyDropdown";
import FontSizeDropdown from "./FontSizeDropdown";
import LineHeightDropdown from "./LineHeightDropdown";
import HeadingDropdown from "./HeadingDropdown";
import TextAlignDropdown from "./TextAlignDropdown";
import ColorPicker from "./ColorPicker";
import FormatPainterButton from "./FormatPainterButton";

const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  toggleFullScreen,
  showInFullScreen,
}) => {
  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkErrors, setLinkErrors] = useState({ text: "", url: "" });
  const [openColorPicker, setOpenColorPicker] = React.useState<string | null>(
    null
  );

  // Track if we're editing an existing link
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [originalLinkData, setOriginalLinkData] = useState<any>(null);

  // Editor state tracking
  const [editorState, setEditorState] = useState({
    bold: false,
    italic: false,
    underline: false,
    bulletList: false,
    orderedList: false,
    taskList: false,
    blockquote: false,
    code: false,
    codeBlock: false,
    link: false,
    canUndo: false,
    canRedo: false,
  });

  // Update toolbar state when editor changes
  useEffect(() => {
    if (!editor) return;

    const updateToolbarState = () => {
      setEditorState({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        bulletList: editor.isActive("bulletList"),
        orderedList: editor.isActive("orderedList"),
        taskList: editor.isActive("taskList"),
        blockquote: editor.isActive("blockquote"),
        code: editor.isActive("code"),
        codeBlock: editor.isActive("codeBlock"),
        link: editor.isActive("link"),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo(),
      });
    };

    editor.on("selectionUpdate", updateToolbarState);
    editor.on("transaction", updateToolbarState);
    updateToolbarState();

    return () => {
      editor.off("selectionUpdate", updateToolbarState);
      editor.off("transaction", updateToolbarState);
    };
  }, [editor]);

  if (!editor) return null;

  // Link handler - detects if clicking on existing link
  const handleLinkAction = (): void => {
    const { from, to } = editor.state.selection;

    // Check if cursor is within a link or link is selected
    const linkMark = editor.getAttributes("link");
    const isOnLink = editor.isActive("link");

    if (isOnLink && linkMark.href) {
      //We're on an existing link - show edit/remove options
      const selectedText =
        editor.state.doc.textBetween(from, to, "") ||
        getLinkTextFromPosition(from, to);

      setIsEditingLink(true);
      setOriginalLinkData({ href: linkMark.href, text: selectedText });
      setLinkText(selectedText);
      setLinkUrl(linkMark.href);
      setLinkErrors({ text: "", url: "" });
      setShowLinkModal(true);
    } else {
      //No existing link - show add link modal
      const selectedText = editor.state.doc.textBetween(from, to, "");

      setIsEditingLink(false);
      setOriginalLinkData(null);
      setLinkText(selectedText || "");
      setLinkUrl("");
      setLinkErrors({ text: "", url: "" });
      setShowLinkModal(true);
    }
  };

  // ✅ Helper function to get link text from cursor position
  const getLinkTextFromPosition = (from: number, to: number): string => {
    let linkText = "";

    editor.state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.marks) {
        const linkMark = node.marks.find((mark) => mark.type.name === "link");
        if (linkMark && node.text) {
          linkText = node.text;
          return false; // Stop iteration
        }
      }
    });

    return linkText;
  };

  // Handle both add and edit link
  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Reset errors
    setLinkErrors({ text: "", url: "" });

    // Validation
    const errors = { text: "", url: "" };

    if (!linkText.trim()) {
      errors.text = "Text is required";
    }

    if (!linkUrl.trim()) {
      errors.url = "URL is required";
    } else if (!isValidUrl(linkUrl.trim())) {
      errors.url = "Please enter a valid URL";
    }

    if (errors.text || errors.url) {
      setLinkErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Process URL
    let finalUrl = linkUrl.trim();
    if (!finalUrl.match(/^https?:\/\//)) {
      finalUrl = `https://${finalUrl}`;
    }

    if (isEditingLink) {
      // ✅ Update existing link
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: finalUrl })
        .insertContent(linkText.trim())
        .run();
    } else {
      // ✅ Insert new link
      if (linkText.trim()) {
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${finalUrl}">${linkText.trim()}</a>`)
          .run();
      } else {
        editor.chain().focus().setLink({ href: finalUrl }).run();
      }
    }

    closeModal();
  };

  // Remove link function
  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    closeModal();
  };

  const closeModal = () => {
    setLinkText("");
    setLinkUrl("");
    setLinkErrors({ text: "", url: "" });
    setIsSubmitting(false);
    setShowLinkModal(false);
    setIsEditingLink(false);
    setOriginalLinkData(null);
  };

  // URL validation helper
  const isValidUrl = (string: string): boolean => {
    try {
      const url = new URL(
        string.match(/^https?:\/\//) ? string : `https://${string}`
      );
      return ["http:", "https:"].includes(url.protocol);
    } catch {
      return false;
    }
  };

  // Other handlers
  const insertTable = (): void => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  const clearFormatting = (): void => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap px-0 rounded-0">
        {/* Full screen toggle*/}
        <ToolbarButton
          onClick={toggleFullScreen}
          title={`${
            showInFullScreen
              ? "Exit Full Screen (ESC or F11 or 'Fn key + F11'"
              : "Full Screen"
          }`}>
          <i
            className={`ti ${
              showInFullScreen ? "ti-maximize-off" : "ti-maximize"
            } fs-16`}></i>
        </ToolbarButton>

        {/* Undo/Redo */}
        <div className="btn-group" role="group">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editorState.canUndo}
            title="Undo (Ctrl+Z)">
            <i className="ti ti-arrow-back-up fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editorState.canRedo}
            title="Redo (Ctrl+Y)">
            <i className="ti ti-arrow-forward-up fs-16"></i>
          </ToolbarButton>
        </div>
        {/* Clear Formatting */}
        <ToolbarButton onClick={clearFormatting} title="Clear Formatting">
          <i className="ti ti-clear-formatting fs-16"></i>
        </ToolbarButton>
        <FormatPainterButton editor={editor} />
        {/* Format & Style */}
        <HeadingDropdown editor={editor} />
        <FontFamilyDropdown editor={editor} />
        <FontSizeDropdown editor={editor} />
        {/* Text Formatting */}
        <div className="btn-group" role="group">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editorState.bold}
            title="Bold (Ctrl+B)">
            <i className="ti ti-bold fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editorState.italic}
            title="Italic (Ctrl+I)">
            <i className="ti ti-italic fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editorState.underline}
            title="Underline (Ctrl+U)">
            <i className="ti ti-underline fs-16"></i>
          </ToolbarButton>
        </div>
        {/* Color Options */}
        <ColorPicker
          editor={editor}
          type="text"
          title="Text Color (Ctrl+Shift+C)"
          icon="ti ti-highlight"
          openColorPicker={openColorPicker}
          setOpenColorPicker={setOpenColorPicker}
        />

        <ColorPicker
          editor={editor}
          type="background"
          title="Background Color (Ctrl+Shift+H)"
          icon="ti ti-palette"
          openColorPicker={openColorPicker}
          setOpenColorPicker={setOpenColorPicker}
        />
        <LineHeightDropdown editor={editor} />
        <TextAlignDropdown editor={editor} />
        {/* Lists */}
        <div className="btn-group" role="group">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editorState.bulletList}
            title="Bullet List (Ctrl+Shift+8)">
            <i className="ti ti-list fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editorState.orderedList}
            title="Letters List (Ctrl+Shift+7)">
            <i className="ti ti-list-letters fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            isActive={editorState.taskList}
            title="Task List">
            <i className="ti ti-list-check fs-16"></i>
          </ToolbarButton>
        </div>
        {/* Block Elements */}
        <div className="btn-group" role="group">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editorState.blockquote}
            title="Quote (Ctrl+Shift+B)">
            <i className="ti ti-quote fs-16"></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editorState.code}
            title="Inline Code (Ctrl+E)">
            <i className="ti ti-code fs-16"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editorState.codeBlock}
            title="Code Block (Ctrl+Alt+C)">
            <i className="ti ti-file-code fs-16"></i>
          </ToolbarButton>
        </div>
        {/* Add/Edit Link Button */}
        <div className="btn-group" role="group">
          <ToolbarButton
            onClick={handleLinkAction}
            isActive={editorState.link}
            title={
              editorState.link ? "Edit Link (Ctrl+K)" : "Insert Link (Ctrl+K)"
            }>
            <i
              className={`ti ${
                editorState.link ? "ti-edit" : "ti-link"
              } fs-16`}></i>
          </ToolbarButton>

          <ToolbarButton
            onClick={insertTable}
            title="Insert Table (Ctrl+Alt+T)">
            <i className="ti ti-table fs-16"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Insert Horizontal Line">
            <i className="ti ti-separator-horizontal fs-16"></i>
          </ToolbarButton>
        </div>
      </div>
      {/* Add/Update Link Modal */}
      <Modal
        show={showLinkModal}
        onHide={closeModal}
        centered
        backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i
              className={`ti ${
                isEditingLink ? "ti-edit" : "ti-link"
              } me-2`}></i>
            {isEditingLink ? "Edit Link" : "Add Link"}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleLinkSubmit}>
          <Modal.Body>
            {/* ✅ Show current link info when editing */}
            {isEditingLink && originalLinkData && (
              <div className="alert alert-info mb-3">
                <small>
                  <strong>Current Link:</strong> {originalLinkData.href}
                </small>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Link Text <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter link text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                isInvalid={!!linkErrors.text}
                disabled={isSubmitting}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">
                {linkErrors.text}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                URL <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                isInvalid={!!linkErrors.url}
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {linkErrors.url}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            {/* ✅ Show Remove Link button when editing */}
            {isEditingLink && (
              <Button
                variant="danger me-auto"
                onClick={handleRemoveLink}
                disabled={isSubmitting}>
                <i className="ti ti-unlink me-1"></i>
                Remove Link
              </Button>
            )}

            <Button
              variant="secondary me-2"
              onClick={closeModal}
              disabled={isSubmitting}>
              <i className="ti ti-x me-1"></i>
              Cancel
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !linkText.trim() || !linkUrl.trim()}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  {isEditingLink ? "Updating..." : "Adding..."}
                </>
              ) : (
                <>
                  <i
                    className={`ti ${
                      isEditingLink ? "ti-check" : "ti-plus"
                    } me-1`}></i>
                  {isEditingLink ? "Update Link" : "Add Link"}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default Toolbar;
